/**
 * AssignmentForm Component
 * Form for adding assignment reason and finalizing the comp assignment
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import { 
  CheckCircle, 
  Movie, 
  Person, 
  Edit,
  Save
} from '@mui/icons-material';
import { formatFilmTitle } from '../../lib/utils/compFormatters';
import { validateAndSanitizeTextInput } from '../../lib/utils/compValidators';
import IMDBInput from './IMDBInput';
import FilmSearch from './FilmSearch';

const AssignmentForm = ({ 
  selectedFilm, 
  assignmentData, 
  onSubmit, 
  loading = false, 
  error = null,
  editingComp = null,
  onIMDBChange = null,
  onFilmLookup = null
}) => {
  const [formData, setFormData] = useState({
    imdbId: '',
    reason: assignmentData.reason || '',
    assignedBy: assignmentData.assignedBy || ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [filmData, setFilmData] = useState(null);

  // Validate form data
  const validateForm = useCallback(() => {
    const errors = {};
    let valid = true;

    // Validate IMDB ID
    if (!formData.imdbId || formData.imdbId.trim().length === 0) {
      errors.imdbId = 'IMDB ID is required';
      valid = false;
    } else if (!/^tt\d{7,8}$/.test(formData.imdbId)) {
      errors.imdbId = 'Invalid IMDB ID format. Use format: tt1234567';
      valid = false;
    }

    // Validate reason
    const reasonValidation = validateAndSanitizeTextInput(formData.reason, {
      minLength: 10,
      maxLength: 500,
      allowEmpty: false
    });

    if (!reasonValidation.isValid) {
      errors.reason = reasonValidation.error;
      valid = false;
    }

    // Validate assigned by (auto-populated, so just check if it exists)
    if (!formData.assignedBy || formData.assignedBy.trim().length === 0) {
      errors.assignedBy = 'User information not available';
      valid = false;
    }

    setValidationErrors(errors);
    setIsValid(valid);
    return valid;
  }, [formData]);

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle IMDB ID change
  const handleIMDBChange = (imdbId) => {
    setFormData(prev => ({
      ...prev,
      imdbId
    }));
    if (onIMDBChange) {
      onIMDBChange(imdbId);
    }
  };

  // Handle film lookup
  const handleFilmLookup = async (imdbId) => {
    if (onFilmLookup) {
      try {
        const film = await onFilmLookup(imdbId);
        setFilmData(film);
        return film;
      } catch (error) {
        setFilmData(null);
        throw error;
      }
    }
  };

  const handleTrackedFilmSelect = (film) => {
    if (!film?.imdbId) return;

    handleIMDBChange(film.imdbId);
    setFilmData({
      filmId: film.imdbId,
      title: film.title,
      releaseYear: film.releaseYear || 'Unknown',
      genre: film.metadata?.genre || film.sourceLabel || 'Tracked Film'
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  // Validate form whenever data changes
  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  // Initialize form data
  useEffect(() => {
    if (assignmentData) {
      setFormData({
        imdbId: assignmentData.imdbId || '',
        reason: assignmentData.reason || '',
        assignedBy: assignmentData.assignedBy || ''
      });
    }
  }, [assignmentData]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {editingComp ? 'Edit Assignment Details' : 'Complete Assignment'}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {editingComp 
          ? 'Update the IMDB ID and reason for this comparison assignment.'
          : 'Enter the IMDB ID of the film to compare and add a reason for the assignment.'
        }
      </Typography>

      {/* Film Search */}
      {!editingComp && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <FilmSearch
            onFilmSelect={handleTrackedFilmSelect}
            loading={loading}
          />
        </Paper>
      )}

      {/* IMDB ID Input */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <IMDBInput
          value={formData.imdbId}
          onChange={handleIMDBChange}
          onFilmLookup={handleFilmLookup}
          loading={loading}
          error={validationErrors.imdbId}
          filmData={filmData}
        />
      </Paper>

      {/* Selected Film Summary (if film data is available) */}
      {filmData && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'success.light', border: '1px solid', borderColor: 'success.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Movie sx={{ color: 'success.main', fontSize: '1.5rem' }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                {formatFilmTitle(filmData.title, 60)}
              </Typography>
              <Typography variant="caption" color="success.main">
                {filmData.filmId} • {filmData.releaseYear} • {filmData.genre}
              </Typography>
            </Box>
            <Chip
              icon={<CheckCircle />}
              label="Ready to Assign"
              color="success"
              size="small"
            />
          </Box>
        </Paper>
      )}

      {/* Assignment Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Assigned By Field - Auto-populated */}
          <TextField
            fullWidth
            label="Assigned By"
            value={formData.assignedBy}
            disabled={true}
            sx={{ mb: 3 }}
            error={!!validationErrors.assignedBy}
            helperText={validationErrors.assignedBy || "Automatically assigned to current user"}
            InputProps={{
              startAdornment: <Person sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />
            }}
          />

          {/* Reason Field */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Assignment Reason"
            placeholder="Explain why this film is a good comparison. Consider factors like genre, target audience, release strategy, budget, or creative elements..."
            value={formData.reason}
            onChange={(e) => handleFieldChange('reason', e.target.value)}
            disabled={loading}
            error={!!validationErrors.reason}
            helperText={
              validationErrors.reason || 
              `${formData.reason.length}/500 characters`
            }
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: <Edit sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} />
            }}
          />

          {/* Character count and validation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Minimum 10 characters required
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {formData.imdbId && /^tt\d{7,8}$/.test(formData.imdbId) && (
                <Chip
                  label="Valid IMDB ID"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {formData.reason.length >= 10 && (
                <Chip
                  label="Valid length"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {formData.assignedBy && (
                <Chip
                  label="User auto-assigned"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <Save />}
              sx={{ minWidth: 120 }}
            >
              {loading 
                ? 'Saving...' 
                : editingComp 
                  ? 'Update Assignment' 
                  : 'Create Assignment'
              }
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Assignment Guidelines */}
      <Paper sx={{ p: 2, mt: 3, backgroundColor: 'info.light', border: '1px solid', borderColor: 'info.main' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main', mb: 1 }}>
          Assignment Guidelines:
        </Typography>
        <Typography variant="caption" color="info.main" component="div">
          • Be specific about why this film is a good comparison<br/>
          • Consider genre, target audience, and release strategy<br/>
          • Mention budget similarities or creative elements<br/>
          • Include any relevant market performance data<br/>
          • Keep the reason concise but informative
        </Typography>
      </Paper>
    </Box>
  );
};

export default AssignmentForm;
